import { useAuth } from '@/store/auth';

function settings() {
  const t = useAuth.getState().tenant;
  return { tz: t?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, locale: t?.locale ?? 'en-IN', currency: t?.currency ?? 'INR', dateFormat: t?.dateFormat ?? 'DD/MM/YYYY' };
}

function parts(d: Date, tz: string) {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { dd: get('day'), mm: get('month'), yyyy: get('year') };
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const { tz, dateFormat } = settings();
  const { dd, mm, yyyy } = parts(d, tz);
  return dateFormat === 'MM/DD/YYYY' ? `${mm}/${dd}/${yyyy}` : dateFormat === 'YYYY-MM-DD' ? `${yyyy}-${mm}-${dd}` : `${dd}/${mm}/${yyyy}`;
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const { tz, locale } = settings();
  return new Intl.DateTimeFormat(locale, { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return `${formatDate(value)} ${formatTime(value)}`;
}

export function formatMoney(amount: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (amount === null || amount === undefined) return '—';
  const { locale, currency } = settings();
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0, notation: opts.compact ? 'compact' : 'standard' }).format(amount);
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat(settings().locale).format(n);
}

export function relativeTime(value: string | Date): string {
  const diff = new Date(value).getTime() - Date.now();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60_000) return 'just now';
  if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (abs < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
  if (abs < 30 * 86_400_000) return rtf.format(Math.round(diff / 86_400_000), 'day');
  return formatDate(value);
}

export function humanize(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Convert a Date/ISO string to a value for <input type="datetime-local"> in the user's local time. */
export function toLocalInput(value: string | Date | null | undefined, withTime = true): string {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
}

export function initials(name: string | null | undefined): string {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/**
 * Format an integer paise amount for display (e.g. 1200050 → "₹12,000.50").
 * Rupees and paise are split with integer arithmetic so no floating-point rounding is involved.
 */
export function formatPaise(paise: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (paise === null || paise === undefined) return '—';
  const { locale, currency } = settings();
  const neg = paise < 0;
  const abs = Math.abs(paise);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  if (opts.compact) return (neg ? '-' : '') + new Intl.NumberFormat(locale, { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(whole);
  const base = new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(whole);
  return `${neg ? '-' : ''}${base}${frac ? `.${String(frac).padStart(2, '0')}` : ''}`;
}

/** Integer paise → editable rupee string ("1200.5" style is avoided: always two decimals when needed). */
export function paiseToInput(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '';
  const whole = Math.floor(Math.abs(paise) / 100);
  const frac = Math.abs(paise) % 100;
  return `${paise < 0 ? '-' : ''}${whole}${frac ? `.${String(frac).padStart(2, '0')}` : ''}`;
}

export function formatBp(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return '—';
  const whole = Math.floor(bp / 100);
  const frac = bp % 100;
  return `${whole}${frac ? `.${String(frac).padStart(2, '0').replace(/0$/, '')}` : ''}%`;
}
